import { useCallback, useEffect, useState } from 'react';
import { api } from './api.ts';
import { live, type LiveStatus } from './live.ts';
import { parsePort, readStoredPort, type ProjectCommand, type ProjectRow } from './project.ts';
import { sessionHasTask, type CommandSnapshot, type GitDiffstat, type Session } from './protocol.ts';

/** Tiny history-API router. A handful of screens do not justify a routing library. */
export function useRoute(): { path: string; go: (path: string) => void } {
  const [path, setPath] = useState(() => location.pathname);
  useEffect(() => {
    const onPop = (): void => setPath(location.pathname);
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);
  const go = (next: string): void => {
    if (next === location.pathname) return;
    history.pushState(null, '', next);
    setPath(next);
  };
  return { path, go };
}

/**
 * Keeps `--app-h` equal to the visible viewport.
 *
 * The viewport meta asks Chrome for `interactive-widget=resizes-content`, but
 * older Android WebViews ignore it and leave the composer under the keyboard;
 * measuring visualViewport covers both cases.
 */
export function useViewportHeight(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const apply = (): void => {
      const h = vv?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--app-h', `${Math.round(h)}px`);
    };
    apply();
    vv?.addEventListener('resize', apply);
    vv?.addEventListener('scroll', apply);
    addEventListener('orientationchange', apply);
    return () => {
      vv?.removeEventListener('resize', apply);
      vv?.removeEventListener('scroll', apply);
      removeEventListener('orientationchange', apply);
    };
  }, []);
}

/** The document scroller — Chrome's native pull-to-refresh is tied to this. */
export function rootScroller(): HTMLElement {
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
}

export function useLiveStatus(): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>(live.status);
  useEffect(() => live.onStatus(setStatus), []);
  return status;
}

/** Working-tree +/- for the selected project. Polls while the screen is on. */
export function useGitDiffstat(projectId: string | null, intervalMs = 2000): GitDiffstat | null {
  const [stat, setStat] = useState<GitDiffstat | null>(null);
  useEffect(() => {
    if (!projectId) {
      setStat(null);
      return;
    }
    let cancelled = false;
    let inFlight = false;
    const load = async (): Promise<void> => {
      if (inFlight || document.visibilityState === 'hidden') return;
      inFlight = true;
      try {
        const next = await api.gitDiffstat(projectId);
        if (!cancelled) setStat(next);
      } catch {
        // Keep the last good reading; a blip should not blank the header.
      } finally {
        inFlight = false;
      }
    };
    setStat(null);
    void load();
    const id = setInterval(() => void load(), intervalMs);
    const onVis = (): void => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [projectId, intervalMs]);
  return stat;
}

function isDoneTask(session: Session): boolean {
  return !session.backlogged && sessionHasTask(session) && session.taskDone;
}

/** Sessions in a project whose saved task ran to success. */
export function countDoneTasks(sessions: readonly Session[], projectId: string | null): number {
  if (!projectId) return 0;
  return sessions.filter((s) => s.projectId === projectId && isDoneTask(s)).length;
}

/** Finished tasks in every project except the selected one. */
export function countDoneTasksOther(sessions: readonly Session[], projectId: string | null): number {
  return sessions.filter((s) => s.projectId !== projectId && isDoneTask(s)).length;
}

/** Finished-task counts keyed by project id. */
export function doneTasksByProject(sessions: readonly Session[]): Readonly<Record<string, number>> {
  const counts: Record<string, number> = {};
  for (const s of sessions) {
    if (!isDoneTask(s)) continue;
    counts[s.projectId] = (counts[s.projectId] ?? 0) + 1;
  }
  return counts;
}

/** Done-task counts for screens that do not already hold the session list. */
export function useDoneTaskCounts(projectId: string | null): {
  selected: number;
  other: number;
  byProject: Readonly<Record<string, number>>;
} {
  const [sessions, setSessions] = useState<Session[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .sessions()
      .then((next) => {
        if (!cancelled) setSessions(next);
      })
      .catch(() => {
        // A missing count is better than an error banner over the chat.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(
    () =>
      live.onSession((updated) =>
        setSessions((prev) =>
          prev.some((s) => s.id === updated.id)
            ? prev.map((s) => (s.id === updated.id ? updated : s))
            : [updated, ...prev],
        ),
      ),
    [],
  );
  return {
    selected: countDoneTasks(sessions, projectId),
    other: countDoneTasksOther(sessions, projectId),
    byProject: doneTasksByProject(sessions),
  };
}

export function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  return days < 7 ? `${days}d` : new Date(ts).toLocaleDateString();
}

/** Wall-clock tick while `active`. Pauses when hidden so idle phones stay idle. */
export function useNow(active = true, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    let id: ReturnType<typeof setInterval> | undefined;
    const tick = (): void => setNow(Date.now());
    const start = (): void => {
      tick();
      id = setInterval(tick, intervalMs);
    };
    const stop = (): void => {
      if (id !== undefined) clearInterval(id);
      id = undefined;
    };
    const onVis = (): void => {
      if (document.visibilityState === 'hidden') stop();
      else start();
    };
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, intervalMs]);
  return now;
}

/** Compact elapsed duration from `ts` to `now`, e.g. `12s`, `3m 4s`, `1h 12m`. */
export function elapsedSince(ts: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = s % 60;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
  }
  const h = Math.floor(m / 60);
  if (h < 24) {
    const rem = m % 60;
    return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
  }
  const d = Math.floor(h / 24);
  const rem = h % 24;
  return rem === 0 ? `${d}d` : `${d}d ${rem}h`;
}

/** Live wait since submit as a clock, e.g. `0:42`, `12:05`, `75:30`. */
export function waitElapsedClock(
  submittedAt: number | null,
  endedAt: number | null,
  waiting: boolean,
  now: number,
): string | null {
  if (submittedAt == null) return null;
  const finished = endedAt != null && endedAt >= submittedAt;
  const until = waiting && !finished ? now : finished ? endedAt : submittedAt;
  const s = Math.max(0, Math.floor((until - submittedAt) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Compact token count for the home list, e.g. `340`, `1.2k`, `12k`, `1.2M`. */
export function formatTokenCount(n: number): string {
  const abs = Math.abs(n);
  if (abs < 1000) return String(Math.round(n));
  if (abs < 10_000) return `${trimDecimal(n / 1000)}k`;
  if (abs < 1_000_000) return `${Math.round(n / 1000)}k`;
  if (abs < 10_000_000) return `${trimDecimal(n / 1_000_000)}M`;
  return `${Math.round(n / 1_000_000)}M`;
}

function trimDecimal(n: number): string {
  return n.toFixed(1).replace(/\.0$/, '');
}

/** Live wait since submit while the agent is busy; frozen at `endedAt` once it finishes. */
export function waitElapsed(
  submittedAt: number | null,
  endedAt: number | null,
  waiting: boolean,
  now: number,
): string | null {
  if (submittedAt == null) return null;
  const finished = endedAt != null && endedAt >= submittedAt;
  const until = waiting && !finished ? now : finished ? endedAt : submittedAt;
  return elapsedSince(submittedAt, until);
}

/** Buzz on run completion. Ignored on desktop and on iOS. */
export function buzz(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* not supported */
  }
}

let clip: HTMLAudioElement | null = null;
let audioUnlocked = false;

function notificationClip(): HTMLAudioElement | null {
  try {
    clip ??= new Audio('/notification.mp3');
    return clip;
  } catch {
    return null;
  }
}

/**
 * Browsers refuse to play until a tap. Prime the clip from a gesture so a
 * later run-finished play() is allowed.
 */
export function unlockAudio(): void {
  const a = notificationClip();
  if (!a || audioUnlocked) return;
  a.muted = true;
  void a
    .play()
    .then(() => {
      a.pause();
      a.currentTime = 0;
      a.muted = false;
      audioUnlocked = true;
    })
    .catch(() => undefined);
}

/** Play the bundled notification clip. No-ops if the element is still locked. */
export function chime(): void {
  const a = notificationClip();
  if (!a) return;
  a.muted = false;
  a.currentTime = 0;
  void a.play().catch(() => undefined);
}

/**
 * Run statuses that mean the chat is still owed an answer. `paused` is one of
 * them: the turn spent the Claude 5-hour window and the gateway is holding it
 * until the window reopens, so nothing has ended and nothing needs a tap.
 */
export function isWaitingStatus(status: string | null | undefined): boolean {
  return status === 'running' || status === 'queued' || status === 'paused';
}

/** True when `runStatus` stopped waiting. First sighting of a session is not a finish. */
export function runJustFinished(
  before: string | null | undefined,
  after: string | null,
): boolean {
  if (before === undefined) return false;
  return isWaitingStatus(before) && !isWaitingStatus(after);
}

/** Vibration + chime for a finished agent run. */
export function notifyRunFinished(): void {
  buzz(30);
  chime();
}

/** Port a command falls back to when nothing was stored and it declares no default. */
const DEFAULT_COMMAND_PORT = 3000;

export type DevServer = {
  /** The project's dev-server command, or null when it has none. */
  command: ProjectCommand | null;
  running: boolean;
  busy: boolean;
  /** Port of the live run, else the one the Commands tab stored. */
  port: number | null;
  /** Starts when stopped, stops when running. Throws so the caller can show the message. */
  toggle: () => Promise<void>;
};

/**
 * Start/stop state for a project's dev server — the first allowlisted desktop
 * command. Reads the port the Commands tab stored, and follows the same live
 * snapshot, so the home FAB and that tab never disagree.
 */
export function useDevServer(project: ProjectRow | null): DevServer {
  const command = project?.commands?.[0] ?? null;
  const projectId = project?.id ?? null;
  const commandId = command?.id ?? null;
  const defaultPort = command?.defaultPort ?? DEFAULT_COMMAND_PORT;
  const [snap, setSnap] = useState<CommandSnapshot | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSnap(null);
    if (!projectId || !commandId) return;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const state = await api.commandState(projectId, commandId);
        if (!cancelled) setSnap(state.snapshot);
      } catch {
        // Keep the last snapshot: a blip must not hide a running dev server.
      }
    };
    void load();
    const offCommand = live.onCommand((next) => {
      if (next.projectId === projectId && next.commandId === commandId) setSnap(next);
    });
    let prev = live.status;
    const offStatus = live.onStatus((status) => {
      if (prev !== 'live' && status === 'live') void load();
      prev = status;
    });
    return () => {
      cancelled = true;
      offCommand();
      offStatus();
    };
  }, [projectId, commandId]);

  const running = snap?.status === 'running';

  const toggle = useCallback(async (): Promise<void> => {
    if (!projectId || !commandId || busy) return;
    const port = parsePort(readStoredPort(projectId, commandId, defaultPort));
    if (port === null) throw new Error('Set a valid port under Config -> Commands first.');
    setBusy(true);
    try {
      setSnap(
        running
          ? await api.stopCommand(projectId, commandId, port)
          : await api.runCommand(projectId, commandId, port),
      );
    } finally {
      setBusy(false);
    }
  }, [projectId, commandId, defaultPort, busy, running]);

  return {
    command,
    running,
    busy,
    port: snap?.port ?? (projectId && commandId ? parsePort(readStoredPort(projectId, commandId, defaultPort)) : null),
    toggle,
  };
}
